import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import type { MenuItem, Branch } from "~/types/new_types";
import GradientCard from "~/components/ui/GradientCard";
import { useBranch } from "~/contexts/BranchContext";
import CircularGradientCard from "~/components/ui/CircularGradientCard";

interface DietaryPreferencesTabContentProps {
  item: MenuItem | null;
  language: string;
}

export const DietaryPreferencesTabContent: React.FC<
  DietaryPreferencesTabContentProps
> = ({ item, language }) => {
  const { t, i18n } = useTranslation();
  const { branch } = useBranch();
  const theme = branch?.theme;

  if (!item) {
    return null;
  }

  const dietaryPreferences = item.dietary_preferences || [];
  const hasDietary = dietaryPreferences && dietaryPreferences.length > 0;

  // Get theme properties
  const primaryColor = theme?.primaryColor;
  const textColor = theme?.textColor;
  const accentColor = theme?.accentColor || theme?.secondaryColor;
  const borderColor = theme?.borderColor;
  const borderRadius = theme?.borderRadius;
  const cardColor = theme?.cardColor;
  const backgroundColor = theme?.backgroundColor;

  // Helper function to get localized dietary preference name and description
    const getDietaryText = (pref: any) => {
    const isArabic = i18n.language === "ar";
    
    if (isArabic && pref.ar) {
      return {
        name: pref.ar.name || pref.name,
        description: pref.ar.description || pref.description,
      };
    }
    
    return {
      name: pref.name,
      description: pref.description,
    };
  };

  // Use translation keys from i18n
  const emptyStateLabel = t("menu.noDietaryPreferences");
  const emptyStateDesc = t("menu.noDietaryPreferencesDesc");
  const infoLabel = t("menu.dietaryInfo");
  const infoDesc = t("menu.dietaryInfoDesc");

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className="space-y-5"
    >
      {/* Dietary Preferences Grid */}
      {hasDietary && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="space-y-3"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dietaryPreferences.map((pref, index) => {
              const { name, description } = getDietaryText(pref);
              return (
                <motion.div
                  key={pref.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.08, duration: 0.3 }}
                >
                  <GradientCard
                    header={
                      <div className="flex items-center gap-2 justify-center">
                        {pref.icon && <span className="text-2xl">{pref.icon}</span>}
                        <span>{name}</span>
                      </div>
                    }
                    description={description}
                    colorFrom={primaryColor}
                    colorTo={accentColor || "#ffffff"}
                  />
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Empty State */}
      {!hasDietary && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <CircularGradientCard
            header={
              <div className="flex items-center gap-2 justify-center">
                <span>{emptyStateLabel}</span>
              </div>
            }
            description={emptyStateDesc}
            colorFrom={primaryColor}
            colorTo={accentColor || "#ffffff"}
          />
        </motion.div>
      )}

    </motion.div>
  );
};
